import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";
import { ROUTE_CONFIG } from "@/routes/config";

/**
 * The way out of onboarding (PREC-3976).
 *
 * `OnboardingLayout` renders no sidebar and no header, so the app's only logout control — the one
 * in `SidebarFooter`, part of `MainLayout` — is unreachable from every screen this layout wraps.
 * A user whose onboarding is unfinished is redirected off `/login` and back into the flow on each
 * visit (`Public.tsx`), and the session survives reloads in localStorage, so without this link the
 * flow is a dead end for anyone who wants to sign in as somebody else.
 *
 * Renders nothing without a session: the same layout also wraps `/forgot-password` and
 * `/set-password`, where there is nobody to sign out.
 */
export const OnboardingExit: FunctionComponent = () => {
	const { t } = useTranslation();
	const { user } = useAppSelector(authSelector);

	if (!user) {
		return null;
	}

	return (
		<div className="fixed right-6 top-4 z-10 flex items-center gap-1.5 text-xs text-text-meta">
			<span>{t("onboarding.exit.signedInAs", { email: user.email })}</span>
			<Link className="font-semibold text-brand-light hover:underline" to={ROUTE_CONFIG.LOGOUT}>
				{t("onboarding.exit.logout")}
			</Link>
		</div>
	);
};
